import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BellIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  HomeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/solid';
import { useLocation, useRouteLoaderData } from '@remix-run/react';
import { CommunityChannelIcon, getPageIcon } from '@shared/dream-components';
import classNames from 'classnames';

import {
  COMMUNITY_ROUTES,
  COMMUNITY_SIDEBAR_WIDTH_CLASS,
  isDirectoryRoute,
  isMessagesRoute,
} from '~/constants/community';
import type { CommunitySpace } from '~/interfaces/community';
import type { RootLoaderData } from '~/root';
import { NectarPageBlock } from '~/utils/nectarPageBlock';

import type { loader as communityLayoutLoader } from '../../../community';
import { useCommunityData } from '../../_context/useCommunityData';
import { useCommunityAccess } from '../../_hooks/useCommunityAccess';
import { useCommunityMembership } from '../../_hooks/useCommunityMembership';
import { useDirectoryName } from '../../_hooks/useDirectoryName';
import { useNavigationV2Enabled } from '../../_hooks/useNavigationV2Enabled';
import { useOpenAnswerQuestionModal } from '../../_hooks/useOpenAnswerQuestionModal';
import { useOpenCommunitySearch } from '../../_hooks/useOpenCommunitySearch';
import { useSpaceAccess } from '../../_hooks/useSpaceAccess';
import { SidebarHeader } from './_components/SidebarHeader';
import { SidebarNavItem } from './_components/SidebarNavItem';
import { SidebarSearch } from './_components/SidebarSearch';
import { SidebarUser } from './_components/SidebarUser';

/** Safely extract pathname from a URL string. Returns null if invalid. */
const getSpacePath = (webUrl: string): string | null => {
  try {
    return new URL(webUrl).pathname;
  } catch {
    return webUrl.startsWith('/') ? webUrl : null;
  }
};

const LockIndicator: FC<{ unlocked?: boolean; label?: string }> = ({ unlocked = false, label }) => {
  const { t } = useTranslation();
  const Icon = unlocked ? LockOpenIcon : LockClosedIcon;

  return (
    <>
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-community-text-on-bg/10">
        <Icon className="size-3 text-community-text-on-bg/50" aria-hidden="true" />
      </span>
      {!unlocked && <span className="sr-only">{label ?? t('community.space.locked')}</span>}
    </>
  );
};

const NewPill: FC = () => {
  const { t } = useTranslation();

  return (
    <span className="shrink-0 rounded-full bg-community-accent px-1.5 py-0.5 text-[10px] font-semibold leading-none text-community-text-on-primary">
      {t('community.space.new')}
    </span>
  );
};

const getLockIndicator = (isLocked: boolean, isGated: boolean): ReactNode => {
  if (isLocked) return <LockIndicator />;
  if (isGated) return <LockIndicator unlocked />;
  return null;
};

interface SidebarProps {
  spaces: CommunitySpace[] | undefined;
  onNavClick?: () => void;
  variant?: 'desktop' | 'mobile';
  notificationUnreadCount?: number | null;
}

export const Sidebar: FC<SidebarProps> = ({ spaces, onNavClick, variant = 'desktop', notificationUnreadCount }) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { canAccessSpace, isSpaceClosed, isSpaceNew, isSpaceUnlisted, profileFieldIdFor } = useSpaceAccess();
  const { isCommunityAccessible } = useCommunityAccess();
  const { isMember, openJoinModal } = useCommunityMembership();
  const directoryName = useDirectoryName();
  const openSearch = useOpenCommunitySearch();
  const openAnswerQuestionModal = useOpenAnswerQuestionModal();
  const navigationV2Enabled = useNavigationV2Enabled();
  const route = useRouteLoaderData<typeof communityLayoutLoader>('routes/community');
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const isLoggedIn = Boolean(rootData?.isLoggedIn);
  const isMessagesEnabled = Boolean(route?.isMessagesEnabled);
  const isDirectoryEnabled = Boolean(route?.community?.directory_enabled);
  const eventsEmbedUrl = route?.eventsEmbedUrl ?? null;
  const unreadSpaceIds = route?.unreadSpaceIds ?? [];
  const contentPages = route?.contentPages ?? [];
  const isMobile = variant === 'mobile';

  const {
    messages: { unreadCount },
  } = useCommunityData();

  return (
    <aside
      className={classNames('flex flex-col justify-between text-community-text-on-bg', {
        [`${COMMUNITY_SIDEBAR_WIDTH_CLASS} shrink-0 p-3 h-full`]: !isMobile,
        'w-full p-4 h-full': isMobile,
      })}
    >
      <div className="flex flex-col gap-4 overflow-y-auto min-h-0" style={{ scrollbarWidth: 'none' }}>
        {!isMobile && !navigationV2Enabled && <SidebarHeader />}
        {!navigationV2Enabled && (
          <SidebarSearch
            onClick={() => {
              onNavClick?.();
              openSearch();
            }}
          />
        )}
        <nav className="flex flex-col gap-1">
          <SidebarNavItem
            icon={<HomeIcon />}
            label={t('community.sidebar.community_feed')}
            to={COMMUNITY_ROUTES.root}
            isActive={pathname === COMMUNITY_ROUTES.root}
            onClick={onNavClick}
          />
          {isDirectoryEnabled && (
            <SidebarNavItem
              icon={<UserGroupIcon />}
              label={directoryName}
              to={COMMUNITY_ROUTES.directory}
              isActive={isDirectoryRoute(pathname)}
              onClick={onNavClick}
            />
          )}
          {isMessagesEnabled && !navigationV2Enabled && (
            <SidebarNavItem
              icon={<ChatBubbleLeftRightIcon />}
              label={t('community.sidebar.messages')}
              to={COMMUNITY_ROUTES.messages}
              isActive={isMessagesRoute(pathname)}
              onClick={onNavClick}
              badgeCount={unreadCount}
              trailingNode={getLockIndicator(!isCommunityAccessible, false)}
            />
          )}
          {contentPages.map((page) => {
            const pagePath = COMMUNITY_ROUTES.page(page.slug);
            const PageIcon = getPageIcon(page.icon);
            const openJoin = () => {
              onNavClick?.();
              openJoinModal(NectarPageBlock.CommunitySidebarPage, pagePath);
            };

            return (
              <SidebarNavItem
                key={page.id}
                icon={<PageIcon />}
                label={page.name}
                to={isMember ? pagePath : undefined}
                isActive={pathname === pagePath || pathname.startsWith(`${pagePath}/`)}
                trailingNode={page.locked && <LockIndicator label={t('community.content_page.locked')} />}
                onClick={isMember ? onNavClick : openJoin}
              />
            );
          })}
          {eventsEmbedUrl && (
            <SidebarNavItem
              icon={<CalendarDaysIcon />}
              label={t('community.sidebar.events')}
              to={COMMUNITY_ROUTES.events}
              isActive={pathname === COMMUNITY_ROUTES.events}
              onClick={onNavClick}
            />
          )}
          {isLoggedIn && !navigationV2Enabled && (
            <SidebarNavItem
              icon={<BellIcon />}
              label={t('community.sidebar.notifications')}
              to={COMMUNITY_ROUTES.notifications}
              isActive={pathname === COMMUNITY_ROUTES.notifications}
              badgeCount={notificationUnreadCount ?? undefined}
              onClick={onNavClick}
            />
          )}
        </nav>
        {spaces != null && spaces.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[13px] md:text-[11px] text-community-text-on-bg/50 px-2 py-1">
              {t('community.sidebar.spaces')}
            </span>
            {spaces.map((space) => {
              const spacePath = getSpacePath(space.web_url);
              if (!spacePath || isSpaceClosed(space) || isSpaceUnlisted(space)) return null;

              const isLocked = !isCommunityAccessible || !canAccessSpace(space);
              const isActive = pathname === spacePath || pathname.startsWith(`${spacePath}/`);
              const isNew = !isActive && isSpaceNew(space);
              const trailingNode = isNew ? <NewPill /> : getLockIndicator(isLocked, space.gated);
              const asksQuestion = isMember && isCommunityAccessible && profileFieldIdFor(space) != null;
              const opensChannel = isMember && !asksQuestion;
              const openGate = () => {
                onNavClick?.();
                if (asksQuestion) openAnswerQuestionModal(space.id);
                else openJoinModal(NectarPageBlock.CommunitySidebarChannel);
              };

              return (
                <SidebarNavItem
                  key={space.id}
                  icon={<CommunityChannelIcon slug={space.slug} emoji={space.emoji} />}
                  label={space.name}
                  tooltip={isMobile ? undefined : (space.description ?? undefined)}
                  to={opensChannel ? spacePath : undefined}
                  isActive={isActive}
                  isUnread={!isNew && unreadSpaceIds.includes(space.id)}
                  trailingNode={trailingNode}
                  onClick={opensChannel ? onNavClick : openGate}
                />
              );
            })}
          </div>
        )}
      </div>
      {(isMobile || !navigationV2Enabled) && <SidebarUser onNavigate={onNavClick} />}
    </aside>
  );
};
